/**
 * KeybookKeyCard — one access key in the credential prompt's key rack.
 *
 * The visual metaphor: credentials are KEYS from your Keybook, drawn as
 * USB-style key fobs (the `.kb-key` CSS paints the body + connector nub
 * with gold contact pins; see index.css "Keybook key rack"). Selecting
 * one "slots it in" — gold ring + glow via `data-active`, and the key
 * icon flips to a checkmark.
 *
 * Layout:  [owner avatar] custom label
 *          [key icon | ✓] CREDENTIAL TYPE
 * The avatar uses the app-wide avatar scheme (avatarBg/getInitials from
 * @papan/core-ui) so the key's owner is the same color-identity here as
 * in every other avatar surface. Incompatible keys stay visible but
 * dimmed (`data-disabled`).
 */
import { Key, Plus, Check } from 'lucide-react'
import { avatarBg, getInitials, GithubIcon } from '@papan/core-ui/ui'

interface Props {
  label: string
  /** Owning email (ownerType='email') or team/role id — drives the avatar */
  owner?: string
  provider?: string | null
  /** password / pat / github / api_key … — shown uppercase on the fob */
  credentialType?: string
  active?: boolean
  /** Incompatible with the requested field shape — dimmed, still visible */
  disabled?: boolean
  /** Dashed "cut a new key" variant */
  isNew?: boolean
  title?: string
  onClick?: () => void
}

function TypeIcon({ provider, isNew, active }: { provider?: string | null; isNew?: boolean; active?: boolean }) {
  if (isNew) return <Plus size={11} className="themed-text-muted shrink-0" />
  if (active) return <Check size={11} className="shrink-0" style={{ color: 'var(--kb-gold)' }} />
  if (provider === 'github') return <GithubIcon size={11} className="themed-text-muted shrink-0" />
  return <Key size={11} className="themed-text-muted shrink-0" />
}

export function KeybookKeyCard({ label, owner, provider, credentialType, active, disabled, isNew, title, onClick }: Props) {
  return (
    <button
      type="button"
      className={`kb-key ${isNew ? 'kb-key-new' : ''}`}
      data-active={active || undefined}
      data-disabled={disabled || undefined}
      title={title ?? (owner ? `${label} — ${owner}` : label)}
      onClick={disabled ? undefined : onClick}
    >
      <span className="flex items-center gap-1.5 w-full min-w-0">
        {owner && (
          <span className={`kb-key-avatar ${avatarBg(owner)}`} aria-hidden>{getInitials(owner)}</span>
        )}
        <span className={`text-[11px] leading-tight truncate ${active ? 'themed-text font-medium' : 'themed-text-secondary'}`}>
          {label}
        </span>
      </span>
      <span className="flex items-center gap-1.5 w-full min-w-0">
        <TypeIcon provider={provider} isNew={isNew} active={active} />
        <span className="text-[9px] uppercase tracking-wider themed-text-muted truncate">
          {isNew ? 'cut a new key' : (credentialType || provider || 'key')}
        </span>
      </span>
    </button>
  )
}
